import { addMonths, format, startOfMonth } from 'date-fns'
import { supabase } from './supabase'
import { addMonthClamped, splitInstallments } from './format'
import type { AccountDraft, AccountEntry, CardDraft, CardEntry, Company, Profile } from './types'

export async function loadProfile(id: string) {
  const { data, error } = await supabase.from('profiles').select('*').eq('id', id).single()
  if (error) throw error
  return data as Profile
}

export async function loadCompanies() {
  const { data, error } = await supabase.from('companies').select('*').eq('active', true).order('sort_order')
  if (error) throw error
  return data as Company[]
}

export async function ensureFixedOccurrences() {
  const until = format(addMonths(new Date(), 24), 'yyyy-MM-dd')
  const { error } = await supabase.rpc('ensure_fixed_occurrences', { p_until: until })
  if (error) throw error
}

export async function loadAccounts() {
  await ensureFixedOccurrences()
  const accounts: AccountEntry[] = []
  const pageSize = 1000

  for (let from = 0; ; from += pageSize) {
    const { data, error } = await supabase
      .from('account_entries')
      .select('*, company:companies(*), series:account_series(*)')
      .order('created_at')
      .order('id')
      .range(from, from + pageSize - 1)
    if (error) throw error

    const page = data as AccountEntry[]
    accounts.push(...page)
    if (page.length < pageSize) break
  }

  return accounts
}

export async function createAccount(draft: AccountDraft, userId: string) {
  const count = draft.kind === 'installment' ? draft.installment_total : 1
  const amounts = draft.kind === 'installment' ? splitInstallments(draft.total_amount, count) : [draft.total_amount]
  const perEntry = amounts[0]
  const { data: series, error: seriesError } = await supabase.from('account_series').insert({
    company_id: draft.company_id,
    kind: draft.kind,
    name: draft.name.trim(),
    notes: draft.notes.trim(),
    amount: perEntry,
    first_due_date: draft.first_due_date,
    due_day: draft.kind === 'single' ? null : draft.due_day,
    installment_total: draft.kind === 'installment' ? count : null,
    created_by: userId,
  }).select().single()
  if (seriesError) throw seriesError

  const rows = Array.from({ length: count }, (_, index) => ({
    series_id: series.id,
    company_id: draft.company_id,
    kind: draft.kind,
    name: draft.name.trim(),
    notes: draft.notes.trim(),
    due_date: addMonthClamped(draft.first_due_date, index, draft.due_day),
    amount: amounts[index],
    installment_number: draft.kind === 'installment' ? index + 1 : null,
    installment_total: draft.kind === 'installment' ? count : null,
  }))
  const { error: entriesError } = await supabase.from('account_entries').insert(rows)
  if (entriesError) {
    await supabase.from('account_series').delete().eq('id', series.id)
    throw entriesError
  }
  if (draft.kind === 'fixed') await ensureFixedOccurrences()
}

export async function payAccount(id: string, paidAt: string | null) {
  const { error } = await supabase.from('account_entries').update({ paid_at: paidAt }).eq('id', id)
  if (error) throw error
}

export async function updateAccount(entry: AccountEntry, values: Partial<AccountEntry>, scope: 'current' | 'future') {
  if (scope === 'current') {
    const { error } = await supabase.from('account_entries').update(values).eq('id', entry.id)
    if (error) throw error
    return
  }

  // A recurring series has one entry per month. Applying the selected due date
  // to every row would make all entries share the same date and violate the
  // unique (series_id, due_date) constraint.
  const { due_date: nextDueDate, ...sharedValues } = values
  const { error: entriesError } = await supabase
    .from('account_entries')
    .update(sharedValues)
    .eq('series_id', entry.series_id)
    .gte('due_date', entry.due_date)
  if (entriesError) throw entriesError

  const seriesValues = Object.fromEntries(Object.entries(sharedValues).filter(([key]) => ['company_id', 'name', 'notes', 'amount'].includes(key))) as Record<string, unknown>

  if (entry.kind === 'fixed' && nextDueDate && nextDueDate !== entry.due_date) {
    const nextDay = Number(nextDueDate.slice(8, 10))
    const { data: futureEntries, error: futureEntriesError } = await supabase
      .from('account_entries')
      .select('id, due_date')
      .eq('series_id', entry.series_id)
      .gte('due_date', entry.due_date)
      .order('due_date')
    if (futureEntriesError) throw futureEntriesError

    for (const futureEntry of futureEntries ?? []) {
      const monthStart = `${futureEntry.due_date.slice(0, 7)}-01`
      const nextDate = addMonthClamped(monthStart, 0, nextDay)
      const { error } = await supabase.from('account_entries').update({ due_date: nextDate }).eq('id', futureEntry.id)
      if (error) throw error
    }
    seriesValues.due_day = nextDay
  }

  if (Object.keys(seriesValues).length) {
    const { error } = await supabase.from('account_series').update(seriesValues).eq('id', entry.series_id)
    if (error) throw error
  }
}

export async function deleteAccount(entry: AccountEntry, scope: 'current' | 'future') {
  if (scope === 'future') {
    const { error } = await supabase.from('account_entries').delete().eq('series_id', entry.series_id).gte('due_date', entry.due_date)
    if (error) throw error
    if (entry.kind === 'fixed') await supabase.from('account_series').update({ ended_on: entry.due_date }).eq('id', entry.series_id)
  } else {
    const { error } = await supabase.from('account_entries').delete().eq('id', entry.id)
    if (error) throw error
  }
}

export async function loadCards(month?: string) {
  let query = supabase.from('card_entries').select('*').order('purchase_date')
  if (month) query = query.eq('billing_month', month)
  const { data, error } = await query
  if (error) throw error
  return data as CardEntry[]
}

export async function createCard(draft: CardDraft, userId: string) {
  const count = draft.kind === 'installment' ? draft.installment_total : 1
  const amounts = splitInstallments(draft.total_amount, count)
  const { data: series, error: seriesError } = await supabase.from('card_series').insert({
    kind: draft.kind,
    name: draft.name.trim(),
    purchase_date: draft.purchase_date,
    first_billing_month: `${draft.first_billing_month.slice(0, 7)}-01`,
    amount: draft.total_amount,
    installment_total: draft.kind === 'installment' ? count : null,
    created_by: userId,
  }).select().single()
  if (seriesError) throw seriesError
  const first = new Date(`${draft.first_billing_month.slice(0, 7)}-01T12:00:00`)
  const rows = amounts.map((amount, index) => ({
    series_id: series.id,
    billing_month: format(startOfMonth(addMonths(first, index)), 'yyyy-MM-dd'),
    purchase_date: draft.purchase_date,
    name: draft.name.trim(),
    amount,
    installment_number: draft.kind === 'installment' ? index + 1 : null,
    installment_total: draft.kind === 'installment' ? count : null,
  }))
  const { error } = await supabase.from('card_entries').insert(rows)
  if (error) {
    await supabase.from('card_series').delete().eq('id', series.id)
    throw error
  }
}

export async function updateCard(entry: CardEntry, values: Partial<CardEntry>, scope: 'current' | 'future') {
  let query = supabase.from('card_entries').update(values)
  query = scope === 'future'
    ? query.eq('series_id', entry.series_id).gte('billing_month', entry.billing_month)
    : query.eq('id', entry.id)
  const { error } = await query
  if (error) throw error
}

export async function deleteCard(entry: CardEntry, scope: 'current' | 'future') {
  let query = supabase.from('card_entries').delete()
  query = scope === 'future'
    ? query.eq('series_id', entry.series_id).gte('billing_month', entry.billing_month)
    : query.eq('id', entry.id)
  const { error } = await query
  if (error) throw error
}
